import type {
  ClientMessage,
  OnlineModeId,
  ServerMessage,
  WorldCard,
  WorldMetric,
  WorldPeriod,
  WorldRecord,
  WorldSeasonPodium,
  WorldWeekWinner,
} from "@golazo/shared";

/**
 * Las estadisticas mundiales, del lado del juego: pedir y esperar.
 *
 * El servidor contesta cada peticion con un mensaje que repite lo pedido
 * (metrica, periodo, modo, amigos, pagina), asi que cada respuesta va a quien
 * la espera aunque se crucen dos. Sin conexion, o si no llega en unos
 * segundos, se resuelve con `null`: la pantalla dice "sin conexion" en vez de
 * quedarse cargando para siempre.
 */
export type WorldBoardMsg = Extract<ServerMessage, { type: "world:board" }>;

export interface BoardRequest {
  metric: WorldMetric;
  period: WorldPeriod;
  mode?: OnlineModeId;
  friends?: boolean;
  offset?: number;
}

export interface WorldService {
  board(req: BoardRequest): Promise<WorldBoardMsg | null>;
  card(clientId: string): Promise<WorldCard | null | undefined>;
  records(): Promise<WorldRecord[] | null>;
  champions(): Promise<{ seasons: WorldSeasonPodium[]; weeks: WorldWeekWinner[] } | null>;
  /** Pedir no salir (o volver a salir) en las clasificaciones. */
  setHidden(hidden: boolean): void;
  hidden(): boolean;
  online(): boolean;
  selfId(): string | null;
  dispose(): void;
}

interface ClientLike {
  send(msg: ClientMessage): void;
  onMessage(fn: (msg: ServerMessage) => void): () => void;
  readonly state: string;
  readonly clientId: string | null;
}

const TIMEOUT_MS = 6000;

const boardKey = (r: { metric: string; period: string; mode?: string; friends?: boolean; offset?: number }): string =>
  `${r.metric}|${r.period}|${r.mode ?? ""}|${r.friends ? 1 : 0}|${r.offset ?? 0}`;

export function createWorldService(client: ClientLike): WorldService {
  let hidden = false;
  const boards = new Map<string, Array<(m: WorldBoardMsg | null) => void>>();
  const cards = new Map<string, Array<(c: WorldCard | null | undefined) => void>>();
  const records: Array<(r: WorldRecord[] | null) => void> = [];
  const champions: Array<(r: { seasons: WorldSeasonPodium[]; weeks: WorldWeekWinner[] } | null) => void> = [];

  const off = client.onMessage((msg) => {
    if (msg.type === "welcome") hidden = msg.worldHidden === true;
    if (msg.type === "world:visibility") hidden = msg.hidden;
    if (msg.type === "world:board") {
      const key = boardKey(msg);
      const waiting = boards.get(key);
      boards.delete(key);
      for (const fn of waiting ?? []) fn(msg);
    }
    if (msg.type === "world:card") {
      const waiting = cards.get(msg.clientId);
      cards.delete(msg.clientId);
      for (const fn of waiting ?? []) fn(msg.card);
    }
    if (msg.type === "world:records") {
      for (const fn of records.splice(0)) fn(msg.records);
    }
    if (msg.type === "world:champions") {
      for (const fn of champions.splice(0)) fn({ seasons: msg.seasons, weeks: msg.weeks });
    }
  });

  function wait<T>(list: Array<(v: T) => void>, fallback: T): Promise<T> {
    return new Promise((resolve) => {
      let done = false;
      const finish = (v: T): void => {
        if (done) return;
        done = true;
        resolve(v);
      };
      list.push(finish);
      setTimeout(() => finish(fallback), TIMEOUT_MS);
    });
  }

  const isOnline = (): boolean => client.state === "online";

  return {
    board(req) {
      if (!isOnline()) return Promise.resolve(null);
      const key = boardKey(req);
      const list = boards.get(key) ?? [];
      boards.set(key, list);
      const promise = wait<WorldBoardMsg | null>(list, null);
      client.send({ type: "world:board", ...req });
      return promise;
    },
    card(clientId) {
      // `undefined` = no se pudo preguntar; `null` = no hay ficha (oculta o no existe).
      if (!isOnline()) return Promise.resolve(undefined);
      const list = cards.get(clientId) ?? [];
      cards.set(clientId, list);
      const promise = wait<WorldCard | null | undefined>(list, undefined);
      client.send({ type: "world:card", clientId });
      return promise;
    },
    records() {
      if (!isOnline()) return Promise.resolve(null);
      const promise = wait<WorldRecord[] | null>(records, null);
      client.send({ type: "world:records" });
      return promise;
    },
    champions() {
      if (!isOnline()) return Promise.resolve(null);
      const promise = wait(champions, null);
      client.send({ type: "world:champions" });
      return promise;
    },
    setHidden(value) {
      hidden = value;
      if (isOnline()) client.send({ type: "world:visibility", hidden: value });
    },
    hidden: () => hidden,
    online: isOnline,
    selfId: () => client.clientId,
    dispose: off,
  };
}
